import { useCallback, useRef } from 'react';

// Web Audio API-based sound effects — no audio files needed
// Generates short synthesized tones for game events

let audioCtx: AudioContext | null = null;

export function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    try {
      audioCtx = new AudioContext();
    } catch {
      return null;
    }
  }
  return audioCtx;
}

function playTone(freq: number, duration: number, type: OscillatorType = 'sine', volume = 0.15) {
  const ctx = getAudioContext();
  if (!ctx) return;

  // Resume if suspended (browser autoplay policy)
  if (ctx.state === 'suspended') {
    ctx.resume();
  }

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.value = volume;
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + duration);
}

function playArpeggio(freqs: number[], interval: number, duration: number, type: OscillatorType = 'sine') {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    ctx.resume();
  }

  freqs.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.value = 0.12;
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * interval + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime + i * interval);
    osc.stop(ctx.currentTime + i * interval + duration);
  });
}

const sounds = {
  cardTap: () => playTone(800, 0.08, 'square', 0.08),
  cardFlip: () => playTone(600, 0.12, 'triangle', 0.06),
  stay: () => playTone(523, 0.15, 'sine', 0.1),
  bust: () => {
    playTone(300, 0.15, 'sawtooth', 0.1);
    setTimeout(() => playTone(200, 0.25, 'sawtooth', 0.08), 100);
  },
  freeze: () => {
    playTone(1200, 0.3, 'sine', 0.1);
    setTimeout(() => playTone(800, 0.4, 'sine', 0.08), 150);
  },
  flip7: () => playArpeggio([523, 659, 784, 1047], 0.1, 0.4, 'triangle'),
  roundEnd: () => playArpeggio([659, 784, 1047], 0.12, 0.3, 'sine'),
  gameOver: () => playArpeggio([523, 659, 784, 1047, 1319], 0.15, 0.5, 'triangle'),
  yourTurn: () => playArpeggio([880, 1109, 1319], 0.08, 0.2, 'sine'),
  nextPlayer: () => playTone(660, 0.1, 'sine', 0.08),
  undo: () => playTone(400, 0.1, 'triangle', 0.06),
};

export type SoundName = keyof typeof sounds;

const MUTE_KEY = 'flip7-muted';

export function isMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setMuted(muted: boolean) {
  try {
    localStorage.setItem(MUTE_KEY, String(muted));
  } catch {
    // localStorage unavailable
  }
}

export function getMuted(): boolean {
  return isMuted();
}

export function useSound() {
  const mutedRef = useRef(isMuted());

  const play = useCallback((name: SoundName) => {
    // Re-read muted state on each play
    mutedRef.current = isMuted();
    if (mutedRef.current) return;

    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    sounds[name]?.();
  }, []);

  return play;
}
